import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Search, CheckCircle, Clock, XCircle } from 'lucide-react';
import api from '../../lib/api';
import { toSlug } from '../../lib/slug';
import { ScriptDraft } from '../../types';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Input from '../../components/ui/Input';

export default function KOLScripts() {
  const navigate = useNavigate();
  const [scripts, setScripts] = useState<ScriptDraft[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  useEffect(() => {
    loadScripts();
  }, []);

  const loadScripts = async () => {
    try {
      const response = await api.get('/scripts');
      setScripts(response.data);
    } catch (error) {
      console.error('Failed to load scripts:', error);
    }
  };

  const filteredScripts = scripts.filter((script) => {
    const matchesSearch = 
      script.job?.title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' || script.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const needsAction = scripts.filter(s => 
    s.status === 'REVISION_REQUESTED' || s.status === 'DRAFT'
  );

  const statusOptions = [
    { value: 'ALL', label: 'All Status' },
    { value: 'DRAFT', label: 'Draft' },
    { value: 'SUBMITTED_FOR_BRAND', label: 'Submitted' },
    { value: 'APPROVED_BY_BRAND', label: 'Approved' },
    { value: 'REVISION_REQUESTED', label: 'Revision Requested' },
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return <Badge variant="secondary">Draft</Badge>;
      case 'SUBMITTED_FOR_BRAND':
        return <Badge variant="default">Submitted</Badge>;
      case 'APPROVED_BY_BRAND':
        return <Badge variant="success">Approved</Badge>;
      case 'REVISION_REQUESTED':
        return <Badge variant="destructive">Revision Requested</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'APPROVED_BY_BRAND':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'REVISION_REQUESTED':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'SUBMITTED_FOR_BRAND':
        return <Clock className="w-5 h-5 text-blue-600" />;
      default:
        return <FileText className="w-5 h-5 text-gray-400" />;
    }
  };

  return (
    <div className="space-y-6 text-glass-text">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="page-title">Scripts</h1>
          <p className="text-glass-text-2 text-sm mt-1">Manage your content scripts</p>
        </div>
      </div>

      {/* Action Alert */}
      {needsAction.length > 0 && (
        <Card className="border-[rgb(var(--warn)/0.35)] bg-[rgb(var(--warn)/0.1)]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-orange-600" />
              <div>
                <p className="font-medium text-orange-900">
                  {needsAction.length} script{needsAction.length !== 1 ? 's need' : ' needs'} your attention
                </p>
                <p className="text-sm text-orange-700">
                  {scripts.filter(s => s.status === 'REVISION_REQUESTED').length > 0 && 
                    'Some scripts need revision. '}
                  {scripts.filter(s => s.status === 'DRAFT').length > 0 && 
                    'Some scripts are in draft.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search by campaign..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="glass-input pl-10"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="glass-input px-4 py-2"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Scripts List */}
      <Card>
        <CardHeader>
          <CardTitle>
            {filteredScripts.length} Script{filteredScripts.length !== 1 ? 's' : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredScripts.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-glass-text-2">
                {searchTerm || statusFilter !== 'ALL'
                  ? 'No scripts match your filters'
                  : 'No scripts yet. Scripts will appear here after you accept invitations.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredScripts.map((script) => (
                <div
                  key={script.id}
                  onClick={() => navigate(`/kol/scripts/${toSlug(script.job?.title || 'script', script.id)}`)}
                  className="flex items-center justify-between p-4 border border-glass rounded-lg hover:bg-glass-3/40 cursor-pointer transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(script.status)}
                      <div>
                        <h3 className="font-semibold text-glass-text">
                          {script.job?.title || 'Untitled Campaign'}
                        </h3>
                        <p className="text-sm text-glass-text-2">
                          Version {script.version} • {script.job?.brandOrg?.name || 'Unknown Client'}
                        </p>
                        <p className="text-xs text-glass-text-3 mt-1">
                          Updated {new Date(script.updatedAt).toLocaleDateString()}
                        </p>
                        {script.reviewComment && (
                          <p className="text-sm text-glass-text-2 mt-2 p-2 bg-glass-3/50 rounded border-l-2 border-accent2">
                            {script.reviewComment}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(script.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

